import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useSaleSelection } from '../hooks/useSaleSelection';
import SaleContextBanner from '../components/admin/SaleContextBanner';

interface Invoice {
  invoiceNumber: string;
  auctionId: string;
  auctionTitle: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  items: Array<{
    lotNumber: string;
    sku: string;
    title: string;
    quantity: number;
    highBid: number;
  }>;
  total: number;
  status: 'unpaid' | 'paid' | 'partial' | 'picked_up';
  pickStatus?: 'pending' | 'picked' | 'ready_for_pickup';
  storageLocation?: string;
  storagePhoto?: string;
  paidAt?: string;
  createdAt: string;
}

const PickupOrders: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'ready' | 'picked_up'>('all');

  /*
   * Third copy of the same loadAuctions(), retired. It ordered on `createdAt`
   * desc and took row 0 — the record's data-entry date, not the sale's. It also
   * inherited the `'latest'` sentinel, which on first render matched EVERY sale
   * and briefly listed pickup orders from across all of them.
   */
  const selection = useSaleSelection({ role: 'fulfillment', storageKey: 'pickupOrders' });
  const { saleId, loading: salesLoading } = selection;

  const loadPickupOrders = useCallback(async () => {
    if (!saleId) { setLoading(false); return; }
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'invoices'));
      const pickupOrders: Invoice[] = [];

      snapshot.forEach(doc => {
        const invoice = doc.data() as Invoice;
        if (invoice.auctionId === saleId &&
            (invoice.pickStatus === 'picked' || invoice.pickStatus === 'ready_for_pickup')) {
          pickupOrders.push(invoice);
        }
      });

      // Sort by bidder number
      pickupOrders.sort((a, b) => parseInt(a.bidderNumber) - parseInt(b.bidderNumber));
      
      setInvoices(pickupOrders);
    } catch (error) {
      console.error('Error loading pickup orders:', error);
    } finally {
      setLoading(false);
    }
  }, [saleId]);

  useEffect(() => { loadPickupOrders(); }, [loadPickupOrders]);

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'ready') return inv.pickStatus === 'ready_for_pickup';
    if (filter === 'picked_up') return inv.status === 'picked_up';
    return true;
  });

  const stats = {
    total: invoices.length,
    picked: invoices.filter(inv => inv.pickStatus === 'picked').length,
    ready: invoices.filter(inv => inv.pickStatus === 'ready_for_pickup').length,
    pickedUp: invoices.filter(inv => inv.status === 'picked_up').length,
  };

  if (salesLoading || loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-gray-600">Loading pickup orders...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:text-blue-800 mb-2"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold">Customer Pickup Orders</h1>
        </div>
      </div>

      {/* Sale in view, selector, and the stale-sale warning. */}
      <SaleContextBanner selection={selection} label="Pickups" />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Ready</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Awaiting Pickup</div>
          <div className="text-2xl font-bold text-blue-600">{stats.picked}</div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Picked Up</div>
          <div className="text-2xl font-bold text-green-600">{stats.ready}</div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Picked Up</div>
          <div className="text-2xl font-bold text-purple-600">{stats.pickedUp}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Filter:
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded font-semibold ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilter('ready')}
            className={`px-4 py-2 rounded font-semibold ${
              filter === 'ready'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Awaiting Pickup ({stats.picked})
          </button>
        </div>
        <div className="text-sm text-gray-600 mt-3">
          Showing {filteredInvoices.length} of {stats.total} orders
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bidder #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Storage
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Items
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredInvoices.map((invoice) => (
              <tr key={invoice.invoiceNumber} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">
                    #{invoice.bidderNumber}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {invoice.bidder.lastName}, {invoice.bidder.firstName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {invoice.storageLocation ? (
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-gray-900">
                        📍 {invoice.storageLocation}
                      </span>
                      {invoice.storagePhoto && (
                        <a
                          href={invoice.storagePhoto}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                          title="View storage photo"
                        >
                          📷
                        </a>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                  {invoice.items.length}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-green-600">
                  ${invoice.total.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="space-y-1">
                    <span
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid' || invoice.paidAt
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {invoice.status === 'paid' || invoice.paidAt ? 'Paid' : 'Unpaid'}
                    </span>
                    <span
                      className={`block px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.pickStatus === 'ready_for_pickup'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {invoice.pickStatus === 'ready_for_pickup'
                        ? '✓ Picked Up'
                        : 'Awaiting Customer'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                  <Link
                    to={`/invoices/${invoice.invoiceNumber}`}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    View
                  </Link>
                  {invoice.pickStatus === 'picked' && (
                    <Link
                      to={`/pickup/${invoice.invoiceNumber}`}
                      className="text-green-600 hover:text-green-900 font-semibold"
                    >
                      📋 Get Signature
                    </Link>
                  )}
                  {invoice.pickStatus === 'ready_for_pickup' && (
                    <span className="text-green-600 font-semibold">
                      ✓ Picked Up
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredInvoices.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No orders match the selected filters
          </div>
        )}
      </div>
    </div>
  );
};

export default PickupOrders;